import debug from "debug";
import * as THREE from "three";

const log = debug("app:os");

/**
 * Class for handling object selection in a 3D scene using mouse and touch events.
 * @category Experience
 * @subcategory ObjectManagement
 */
class ObjectSelector {
  /**
   * Initializes the ObjectSelector with the given experience instance.
   * @param {Object} experience - The experience instance containing scene, camera, and eventHub.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.camera = experience.camera.instance;
    this.renderer = experience.renderer.instance;
    this.eventHub = experience.eventHub;
    this.raycaster = new THREE.Raycaster();
    this.isDragging = false;
    this.startTime = 0;
    this.startPosition = { x: 0, y: 0 };
    this.clickThreshold = 200;
    this.moveThreshold = 5;

    this.listenForEvents();
  }

  cleanup() {
    this.eventHub.off("globalMouseDown", this.handleMouseDown);
    this.eventHub.off("globalMouseUp", this.handleMouseUp);
    this.eventHub.off("globalTouchStart", this.handleTouchStart);
    this.eventHub.off("globalTouchEnd", this.handleTouchEnd);
    this.eventHub.off("globalKeyDown", this.handleKeyDown);
    this.eventHub.off("globalDoubleClick");
    this.eventHub.off("globalContextMenu", this.handleContextMenu);

    if (this.boundHandleMouseMove) {
      this.eventHub.off("globalMouseMove", this.boundHandleMouseMove);
    }
    if (this.boundHandleTouchMove) {
      this.eventHub.off("globalTouchMove", this.boundHandleTouchMove);
    }

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.eventHub = null;
    this.raycaster = null;
    this.isDragging = false;
    this.startPosition = null;

    log("ObjectSelector instance cleaned up.");
  }

  /**
   * Sets up event listeners for global mouse, touch, and keyboard interactions.
   */
  listenForEvents() {
    this.eventHub.on("globalMouseDown", this.handleMouseDown.bind(this));
    this.eventHub.on("globalMouseUp", this.handleMouseUp.bind(this));
    this.eventHub.on("globalTouchStart", this.handleTouchStart.bind(this));
    this.eventHub.on("globalTouchEnd", this.handleTouchEnd.bind(this));
    this.eventHub.on("globalKeyDown", this.handleKeyDown.bind(this));

    this.eventHub.on("globalDoubleClick", () => {
      this.doubleClickDetected = true;
    });
    this.eventHub.on("globalContextMenu", this.handleContextMenu.bind(this));
  }

  /**
   * Handles the "Ctrl+A" or "Cmd+A" key combination to select all objects in the scene.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
      event.preventDefault();
      this.selectAll();
    }
  }

  /**
   * Selects all objects in the scene that are meshes or groups.
   */
  selectAll() {
    const allObjects = this.scene.children.filter(
      (child) => child.isMesh || child.isGroup
    );
    this.eventHub.emit("multiSelect", allObjects);
    log("All objects selected:", allObjects);
  }

  /**
   * Handles the mouse down event for initiating selection or dragging.
   * @param {MouseEvent} event - The mouse down event.
   */
  handleMouseDown(event) {
    if (event.button === 2 || event.button === 1 || event.target !== this.experience.canvas) return;
  
    this.startTime = Date.now();
    this.startPosition = { x: event.clientX, y: event.clientY };
    this.isDragging = false;
  
    // 💡 Track initial intersected object
    this.dragStartIntersect = this.raycastAndGetParentObject(event);
  
    // 💡 Pass original event to MoveObject
    this.experience.latestMouseEvent = event;
  
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.eventHub.on("globalMouseMove", this.boundHandleMouseMove);
  }
  
  
  
  

  /**
   * Handles the mouse up event to finalize selection or drag operations.
   * @param {MouseEvent} event - The mouse up event.
   */
  handleMouseUp(event) {
    this.eventHub.off("globalMouseMove", this.boundHandleMouseMove);
  
    const endTime = Date.now();
    const duration = endTime - this.startTime;
    const distance = this.calculateDistance(event.clientX, event.clientY);
  
    if (!this.isDragging && duration < this.clickThreshold && distance < this.moveThreshold) {
      const intersectedObject = this.raycastAndGetParentObject(event);
      const modifiersOn = event.ctrlKey || event.metaKey || event.shiftKey;
  
      if (!intersectedObject) {
        if (modifiersOn) {
          this.eventHub.emit("modifierSelect", null); // keep current selection
        } else {
          this.eventHub.emit("clearAll", event); // clear everything
        }
      } else {
        if (modifiersOn) {
          this.eventHub.emit("modifierSelect", intersectedObject); // 💥 ADD object to selection
        } else {
          this.eventHub.emit("singleSelect", intersectedObject); // 👈 Replace selection
        }
      }
    }
  
    this.isDragging = false;
  }
  
  
  

  /**
   * Handles the mouse move event for updating drag operations.
   * @param {MouseEvent} event - The mouse move event.
   */
  handleMouseMove(event) {
    const distance = this.calculateDistance(event.clientX, event.clientY);
    if (distance > this.moveThreshold) {
      this.isDragging = true;
  
      if (this.dragStartIntersect) {
        const modifiersOn = event.ctrlKey || event.metaKey || event.shiftKey;
        log("modifier keys held?", modifiersOn);
        log("dragStartIntersect object:", this.dragStartIntersect);
  
        if (modifiersOn) {
          log("🔶 Emitting modifierDragSelect");
          this.eventHub.emit("modifierDragSelect", this.dragStartIntersect);
        } else {
          log("🔷 Emitting singleDragSelect");
          this.eventHub.emit("singleDragSelect", this.dragStartIntersect);
        }
      } else {
        log("⬜ No intersected object – starting marquee");
        this.eventHub.emit("clearAll", event);
        this.eventHub.emit("marqueeStarted", {
          startX: this.startPosition.x,
          startY: this.startPosition.y,
          button: event.buttons,
        });
      }
  
      this.eventHub.off("globalMouseMove", this.boundHandleMouseMove);
    }
  }
  
  
  
  
  
  

  /**
   * Handles the touch start event for initiating touch-based selection or dragging.
   * @param {TouchEvent} event - The touch start event.
   */
  handleTouchStart(event) {
    if (event.target !== this.experience.canvas) return;
    this.startTime = Date.now();
    const touch = event.touches[0];
    this.startPosition = { x: touch.clientX, y: touch.clientY };
    this.isDragging = false;
    this.boundHandleTouchMove = this.handleTouchMove.bind(this);
    // Add touchmove event listener
    this.eventHub.on("globalTouchMove", this.boundHandleTouchMove);
  }

  /**
   * Handles the touch move event for updating drag or swipe operations.
   * @param {TouchEvent} event - The touch move event.
   */
  handleTouchMove(event) {
    const touch = event.touches[0];
    const distance = this.calculateDistance(touch.clientX, touch.clientY);
    if (distance > this.moveThreshold) {
      this.isDragging = true;

      // Handle swipe logic here
      this.handleDrag(touch);

      // Remove touchmove event listener immediately
      this.eventHub.off("globalTouchMove", this.boundHandleTouchMove);
    }
  }

  /**
   * Handles the touch end event to finalize touch-based selection or drag operations.
   * @param {TouchEvent} event - The touch end event.
   */
  handleTouchEnd(event) {
    // Remove touchmove event listener
    this.eventHub.off("globalTouchMove", this.boundHandleTouchMove);

    const endTime = Date.now();
    const duration = endTime - this.startTime;
    const touch = event.changedTouches[0];
    const distance = this.calculateDistance(touch.clientX, touch.clientY);

    if (
      !this.isDragging &&
      duration < this.clickThreshold &&
      distance < this.moveThreshold
    ) {
      this.handleClick(touch);
    }
    // Reset dragging state for next interaction
    this.isDragging = false;
  }

  /**
   * Calculates the distance between the start and current positions of a mouse or touch event.
   * @param {number} x - The current x-coordinate.
   * @param {number} y - The current y-coordinate.
   * @returns {number} The distance between the start and current positions.
   */
  calculateDistance(x, y) {
    return Math.sqrt(
      Math.pow(x - this.startPosition.x, 2) +
        Math.pow(y - this.startPosition.y, 2)
    );
  }

  /**
   * Handles a single click event to select or deselect objects in the scene.
   * @param {MouseEvent|TouchEvent} event - The click or touch event.
   */
  handleClick(event) {
    if (event.button === 2 || this.doubleClickDetected) {
      this.doubleClickDetected = false; // Reset flag after skipping
      return; // Ignore right-clicks and skip single-click action on double-click
    }
    log("click", event.button);

    // Single-click logic here
    const intersectedObject = this.raycastAndGetParentObject(event);
    const modifiersOn = event.ctrlKey || event.metaKey || event.shiftKey;

    if (!intersectedObject) {
      if (modifiersOn) {
        this.eventHub.emit("modifierSelect", null);
      } else {
        this.eventHub.emit("clearAll", event);
      }
    } else {
      if (modifiersOn) {
        this.eventHub.emit("modifierSelect", intersectedObject);
      } else {
        this.eventHub.emit("singleSelect", intersectedObject);
      }
    }
  }

  /**
   * Handles the context menu event (right-click) for object selection.
   * @param {MouseEvent} event - The context menu event.
   */
  handleContextMenu(event) {
    log("contextmenu", event.button);
    const intersectedObject = this.raycastAndGetParentObject(event);
    log("intersectedobject", intersectedObject);
    if (!intersectedObject) return;
    this.eventHub.emit("clearAll", event); // Do nothing if no object or already selected
    log("contextmenu after", event.button);
    this.eventHub.emit("singleSelect", intersectedObject); // Select only unselected objects
  }

  /**
   * Handles drag operations for selecting or moving objects in the scene.
   * @param {MouseEvent|TouchEvent} event - The drag event.
   */
  handleDrag(event) {
    if (event.buttons === 2) return;
    const intersectedObject = this.raycastAndGetParentObject(event);
    log("intersectedobject", intersectedObject);
    const modifiersOn = event.ctrlKey || event.metaKey || event.shiftKey;

    if (!intersectedObject) {
      if (modifiersOn) {
        this.eventHub.emit("modifierSelect", null);
        this.eventHub.emit("modifierMarqueeStarted", {
          startX: this.startPosition.x,
          startY: this.startPosition.y,
          button: event.buttons,
        });
      } else {
        this.eventHub.emit("clearAll", event);
        this.eventHub.emit("marqueeStarted", {
          startX: this.startPosition.x,
          startY: this.startPosition.y,
          button: event.buttons,
        });
      }
    } else {
      if (modifiersOn) {
        this.eventHub.emit("modifierDragSelect", intersectedObject);
      } else {
        this.eventHub.emit("singleDragSelect", intersectedObject);
      }
    }
  }

  /**
   * Performs a raycast to detect the parent object of the intersected point in the scene.
   * @param {MouseEvent|TouchEvent} event - The event containing the point to raycast.
   * @returns {THREE.Group|null} The parent group of the intersected object, or null if no intersection.
   */
  raycastAndGetParentObject(event) {
    const mouse = new THREE.Vector2(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1
    );
  
    this.raycaster.setFromCamera(mouse, this.camera);
    
    // ✅ Filter out axes helpers and other helper objects
    const sceneObjects = this.scene.children.filter((obj) => {
      return !(obj instanceof THREE.AxesHelper);
    });
    
    const intersects = this.raycaster.intersectObjects(sceneObjects, true);
  
    if (!intersects.length) return null;
  
    let basePlaneHit = null;
  
    for (let i = 0; i < intersects.length; i++) {
      const obj = intersects[i].object;
      
      // ✅ Skip axes helpers even if they somehow got through
      if (obj instanceof THREE.AxesHelper) {
        continue;
      }
      
      const isBasePlane = obj.name === "BasePlane";
  
      if (!isBasePlane) {
        // ✅ Found a real object
        let parent = obj;
        while (parent) {
          if (parent.isGroup && parent.name === "XMO") return parent;
          parent = parent.parent;
        }
        return null;
      } else {
        basePlaneHit = intersects[i];
      }
    }
  
    // Only basePlane was hit
    return null;
  }
  
  
}

export default ObjectSelector;
